import React, { useState, useEffect } from 'react';
import { mcpClient, StockQuote, TrendingData, MarketIndex, CurrencyRate, NewsItem } from './services/mcpClient';
import StockQuoteCard from './components/StockQuoteCard';
import TrendingStocks from './components/TrendingStocks';
import MarketIndices from './components/MarketIndices';
import CurrencyRates from './components/CurrencyRates';
import FinancialNews from './components/FinancialNews';
import ChatInterface from './components/ChatInterface';
import LoadingSpinner from './components/LoadingSpinner';
import ErrorMessage from './components/ErrorMessage';
import './App.css';

const App: React.FC = () => {
  // State management
  const [symbols, setSymbols] = useState<string>('TSLA');
  // Region and language removed from new design
  const [quotes, setQuotes] = useState<StockQuote[]>([]);
  const [trendingData, setTrendingData] = useState<TrendingData | null>(null);
  const [marketIndices, setMarketIndices] = useState<MarketIndex[]>([]);
  const [currencyRate, setCurrencyRate] = useState<CurrencyRate | null>(null);
  const [financialNews, setFinancialNews] = useState<NewsItem[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [serverStatus, setServerStatus] = useState<string>('checking');
  
  // New tool states
  const [fromCurrency, setFromCurrency] = useState<string>('USD');
  const [toCurrency, setToCurrency] = useState<string>('EUR');
  const [newsSymbol, setNewsSymbol] = useState<string>('');
  const [newsCount, setNewsCount] = useState<number>(5);

  // Check server health on component mount
  useEffect(() => {
    checkServerHealth();
  }, []);

  // Auto-load Tesla stock on mount
  useEffect(() => {
    if (serverStatus === 'healthy') {
      handleGetQuotes();
    }
  }, [serverStatus]); // eslint-disable-line react-hooks/exhaustive-deps

  const checkServerHealth = async () => {
    try {
      console.log('🔍 Checking MCP server health...');
      const healthData = await mcpClient.healthCheck();
      console.log('✅ MCP server is healthy:', healthData);
      setServerStatus('healthy');
    } catch (error) {
      console.error('❌ MCP server health check failed:', error);
      setServerStatus('unhealthy');
      setError(`Unable to connect to MCP server: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  const handleGetQuotes = async () => {
    if (!symbols.trim()) {
      setError('Please enter at least one stock symbol');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const response = await mcpClient.getStockQuotes(symbols, 'US', 'en');
      if (response.success) {
        setQuotes(response.data);
      } else {
        setError(response.error || 'Failed to fetch stock quotes');
      }
    } catch (error) {
      setError(error instanceof Error ? error.message : 'An unexpected error occurred');
    } finally {
      setIsLoading(false);
    }
  };

  const handleGetTrending = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await mcpClient.getTrendingStocks('US');
      if (response.success) {
        setTrendingData(response.data);
      } else {
        setError(response.error || 'Failed to fetch trending stocks');
      }
    } catch (error) {
      setError(error instanceof Error ? error.message : 'An unexpected error occurred');
    } finally {
      setIsLoading(false);
    }
  };

  const handleRetry = () => {
    setError(null);
    if (quotes.length > 0) {
      handleGetQuotes();
    } else if (trendingData) {
      handleGetTrending();
    } else {
      checkServerHealth();
    }
  };

  const handleSymbolsChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSymbols(e.target.value.toUpperCase());
  };

  // Region and language handlers removed as they're not used in the new design

  // New tool handlers
  const handleGetMarketIndices = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await mcpClient.getMarketIndices(['^GSPC', '^DJI', '^IXIC', '^VIX'], 'US');
      if (response.success) {
        setMarketIndices(response.data);
      } else {
        setError(response.error || 'Failed to fetch market indices');
      }
    } catch (error) {
      setError(error instanceof Error ? error.message : 'An unexpected error occurred');
    } finally {
      setIsLoading(false);
    }
  };

  const handleGetCurrencyRates = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await mcpClient.getCurrencyRates(fromCurrency, toCurrency, 'US');
      if (response.success) {
        setCurrencyRate(response.data);
      } else {
        setError(response.error || 'Failed to fetch currency rates');
      }
    } catch (error) {
      setError(error instanceof Error ? error.message : 'An unexpected error occurred');
    } finally {
      setIsLoading(false);
    }
  };

  const handleGetFinancialNews = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await mcpClient.getFinancialNews(
        newsSymbol || undefined, 
        newsCount, 
        'US'
      );
      if (response.success) {
        setFinancialNews(response.data.news);
      } else {
        setError(response.error || 'Failed to fetch financial news');
      }
    } catch (error) {
      setError(error instanceof Error ? error.message : 'An unexpected error occurred');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="app">
      {/* Top Navigation Bar */}
      <nav className="top-nav">
        <div className="nav-content">
          <div className="nav-left">
            <div className="logo">
              <span className="logo-icon">📈</span>
              <span className="logo-text">FinanceHub</span>
            </div>
          </div>
          <div className="nav-center">
            <div className="search-bar">
              <input
                type="text"
                placeholder="Search stocks, ETFs, crypto..."
                className="search-input"
                value={symbols}
                onChange={handleSymbolsChange}
              />
              <button 
                onClick={handleGetQuotes}
                disabled={isLoading || serverStatus !== 'healthy'}
                className="search-btn"
              >
                🔍
              </button>
            </div>
          </div>
          <div className="nav-right">
            <div className={`connection-status ${serverStatus}`}>
              <span className="status-dot"></span>
              <span className="status-text">
                {serverStatus === 'healthy' ? 'Live' : 
                 serverStatus === 'unhealthy' ? 'Offline' : 'Connecting...'}
              </span>
            </div>
            {serverStatus === 'unhealthy' && (
              <button 
                onClick={checkServerHealth}
                className="retry-btn"
              >
                Retry
              </button>
            )}
          </div>
        </div>
      </nav>

      <main className="app-main">
        {/* Dashboard Layout */}
        <div className="dashboard">
          {/* Left Sidebar */}
          <aside className="sidebar">
            <div className="sidebar-section">
              <h3>Quick Actions</h3>
              <div className="action-buttons">
                <button
                  onClick={handleGetTrending}
                  disabled={isLoading || serverStatus !== 'healthy'}
                  className="action-btn"
                >
                  <span className="btn-icon">🔥</span>
                  <span className="btn-text">Trending</span>
                </button>
                <button
                  onClick={handleGetMarketIndices}
                  disabled={isLoading || serverStatus !== 'healthy'}
                  className="action-btn"
                >
                  <span className="btn-icon">📊</span>
                  <span className="btn-text">Indices</span>
                </button>
                <button
                  onClick={handleGetFinancialNews}
                  disabled={isLoading || serverStatus !== 'healthy'}
                  className="action-btn"
                >
                  <span className="btn-icon">📰</span>
                  <span className="btn-text">News</span>
                </button>
              </div>
            </div>

            <div className="sidebar-section">
              <h3>Currency Exchange</h3>
              <div className="currency-widget">
                <div className="currency-row">
                  <select
                    value={fromCurrency}
                    onChange={(e) => setFromCurrency(e.target.value)}
                    className="currency-select-small"
                  >
                    <option value="USD">USD</option>
                    <option value="EUR">EUR</option>
                    <option value="GBP">GBP</option>
                    <option value="JPY">JPY</option>
                    <option value="CAD">CAD</option>
                    <option value="AUD">AUD</option>
                  </select>
                  <span className="currency-arrow">→</span>
                  <select
                    value={toCurrency}
                    onChange={(e) => setToCurrency(e.target.value)}
                    className="currency-select-small"
                  >
                    <option value="USD">USD</option>
                    <option value="EUR">EUR</option>
                    <option value="GBP">GBP</option>
                    <option value="JPY">JPY</option>
                    <option value="CAD">CAD</option>
                    <option value="AUD">AUD</option>
                  </select>
                </div>
                <button
                  onClick={handleGetCurrencyRates}
                  disabled={isLoading || serverStatus !== 'healthy'}
                  className="currency-btn"
                >
                  Get Rate
                </button>
              </div>
            </div>

            <div className="sidebar-section">
              <h3>News Settings</h3>
              <div className="news-widget">
                <input
                  type="text"
                  value={newsSymbol}
                  onChange={(e) => setNewsSymbol(e.target.value.toUpperCase())}
                  placeholder="Symbol (optional)"
                  className="news-input-small"
                />
                <select
                  value={newsCount}
                  onChange={(e) => setNewsCount(parseInt(e.target.value))}
                  className="news-count-small"
                >
                  <option value={3}>3 articles</option>
                  <option value={5}>5 articles</option>
                  <option value={10}>10 articles</option>
                </select>
              </div>
            </div>
          </aside>

          {/* Main Content Area */}
          <div className="main-content">
            {/* Market Overview Cards */}
            <div className="market-overview">
              <div className="overview-card">
                <div className="card-header">
                  <h3>Market Status</h3>
                  <span className={`market-status ${serverStatus}`}>
                    {serverStatus === 'healthy' ? '🟢 Live' : '🔴 Offline'}
                  </span>
                </div>
                <div className="card-content">
                  <p>Real-time market data powered by Yahoo Finance API</p>
                </div>
              </div>
              
              <div className="overview-card">
                <div className="card-header">
                  <h3>Quick Stats</h3>
                </div>
                <div className="card-content">
                  <div className="stats-grid">
                    <div className="stat-item">
                      <span className="stat-label">Quotes Loaded</span>
                      <span className="stat-value">{quotes.length}</span>
                    </div>
                    <div className="stat-item">
                      <span className="stat-label">News Articles</span>
                      <span className="stat-value">{financialNews.length}</span>
                    </div>
                    <div className="stat-item">
                      <span className="stat-label">Indices</span>
                      <span className="stat-value">{marketIndices.length}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Content Sections */}
            <div className="content-sections">
              {isLoading && <LoadingSpinner message="Fetching data from remote MCP server..." />}
              
              {error && (
                <ErrorMessage 
                  error={error} 
                  onRetry={handleRetry}
                />
              )}

              {!isLoading && !error && quotes.length > 0 && (
                <section className="content-section">
                  <div className="section-header">
                    <h2>Stock Quotes</h2>
                    <span className="section-count">{quotes.length} stocks</span>
                  </div>
                  <div className="quotes-grid">
                    {quotes.map((quote) => (
                      <StockQuoteCard key={quote.symbol} quote={quote} />
                    ))}
                  </div>
                </section>
              )}

              {!isLoading && !error && trendingData && (
                <section className="content-section">
                  <div className="section-header">
                    <h2>Trending Stocks</h2>
                    <span className="section-count">{trendingData.count} trending</span>
                  </div>
                  <TrendingStocks trendingData={trendingData} />
                </section>
              )}

              {!isLoading && !error && marketIndices.length > 0 && (
                <section className="content-section">
                  <div className="section-header">
                    <h2>Market Indices</h2>
                    <span className="section-count">{marketIndices.length} indices</span>
                  </div>
                  <MarketIndices 
                    indices={marketIndices} 
                    loading={false} 
                    error={null} 
                  />
                </section>
              )}

              {!isLoading && !error && currencyRate && (
                <section className="content-section">
                  <div className="section-header">
                    <h2>Currency Exchange</h2>
                    <span className="section-count">{currencyRate.fromCurrency} → {currencyRate.toCurrency}</span>
                  </div>
                  <CurrencyRates 
                    currencyRate={currencyRate} 
                    loading={false} 
                    error={null} 
                  />
                </section>
              )}

              {!isLoading && !error && financialNews.length > 0 && (
                <section className="content-section">
                  <div className="section-header">
                    <h2>Financial News</h2>
                    <span className="section-count">{financialNews.length} articles</span>
                  </div>
                  <FinancialNews 
                    news={financialNews} 
                    loading={false} 
                    error={null} 
                  />
                </section>
              )}

              {!isLoading && !error && quotes.length === 0 && !trendingData && serverStatus === 'healthy' && (
                <section className="welcome-section">
                  <div className="welcome-content">
                    <h2>Welcome to FinanceHub</h2>
                    <p>Search for stocks, ETFs, or cryptocurrencies to get started with real-time market data.</p>
                    <div className="quick-symbols">
                      <h3>Popular Symbols</h3>
                      <div className="symbol-tags">
                        {['TSLA', 'AAPL', 'MSFT', 'GOOGL', 'AMZN', 'NVDA', 'META', 'BTC-USD'].map((symbol) => (
                          <button
                            key={symbol}
                            className="symbol-tag"
                            onClick={() => setSymbols(symbol)}
                          >
                            {symbol}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                </section>
              )}
            </div>

            {/* Chat Interface Section */}
            <section className="chat-section">
              <div className="section-header">
                <h2>💬 Financial Assistant</h2>
                <p>Ask me anything about stocks, market data, or financial news!</p>
              </div>
              <ChatInterface />
            </section>
          </div>
        </div>
      </main>

      <footer className="app-footer">
        <p>
          Powered by <strong>Cloudflare Workers</strong> • 
          Yahoo Finance API • 
          MCP Protocol
        </p>
      </footer>
    </div>
  );
};

export default App;